import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook to debounce a value.
 * @param value The value to debounce.
 * @param wait The debounce delay in milliseconds.
 * @param options:{leading: boolean} Options for the debounce behavior.
 * @returns A tuple containing the debounced value and a cancel function.
 */
export const useDebouncedValue = (value, wait, options = { leading: false }) => {
    const [_value, setValue] = useState(value);
    const mountedRef = useRef(false);
    const timeOutRef = useRef(null);
    const coolDownRef = useRef(false);

    const cancel = () => window.clearTimeout(timeOutRef.current);

    useEffect(() => {
        if (mountedRef.current) {
            if (!coolDownRef.current && options.leading) {
                coolDownRef.current = true;
                setValue(value);
            } else {
                cancel();
                timeOutRef.current = window.setTimeout(() => {
                    coolDownRef.current = false;
                    setValue(value);
                }, wait);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, options.leading, wait]);

    useEffect(() => {
        mountedRef.current = true;
        return cancel;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return [_value, cancel];
};
